import { useEffect, useMemo, useState } from 'react'

import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'

import { assignSeatLabels } from '../../../../../../../../utilities/api'
import {
  flattenSelectedSeatIds,
  labelsForSeatIds,
  PRESET_SEAT_LABELS,
} from '../../../../../../../../utilities/seatLabels'

import './seatLabelsForm.scss'

export default function SeatLabelsForm({
  eventUuid,
  selectedSeatIds,
  seatLabels,
  onLabelsChange,
  disabled = false,
}) {
  const seatIds = useMemo(
    () => flattenSelectedSeatIds(selectedSeatIds),
    [selectedSeatIds]
  )
  const current = useMemo(
    () => labelsForSeatIds(seatIds, seatLabels),
    [seatIds, seatLabels]
  )
  const [label, setLabel] = useState(current.label)
  const [scope, setScope] = useState('event')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    setLabel(current.mixed ? '' : current.label)
    setError(null)
  }, [current.label, current.mixed, seatIds.join('|')])

  if (!eventUuid || seatIds.length === 0) return null

  const handleApply = (nextLabel) => {
    if (disabled || saving) return
    setSaving(true)
    setError(null)
    assignSeatLabels({
      eventUUID: eventUuid,
      seatIds,
      label: nextLabel,
      scope,
    })
      .then((res) => {
        const nextLabels = res?.data?.seatLabels
        if (nextLabels && typeof nextLabels === 'object') {
          onLabelsChange?.(nextLabels)
        } else {
          onLabelsChange?.({
            ...seatLabels,
            ...Object.fromEntries(
              seatIds.map((seatId) => [seatId, nextLabel || undefined])
            ),
          })
        }
        setLabel(nextLabel || '')
      })
      .catch((err) => {
        setError(
          err?.response?.data?.error?.message ||
            err?.response?.data?.message ||
            err?.message ||
            'Could not save seat labels.'
        )
      })
      .finally(() => {
        setSaving(false)
      })
  }

  return (
    <div className="seat-labels-form">
      <h2 className="seat-labels-form__title">Seat label</h2>
      <p className="seat-labels-form__hint">
        {current.mixed
          ? 'Selected seats have different labels.'
          : current.label
            ? `Current: ${current.label}`
            : 'Optional note buyers will see, such as obstructed view.'}
      </p>

      <div className="seat-labels-form__presets">
        {PRESET_SEAT_LABELS.map((preset) => (
          <Button
            key={preset}
            type="button"
            size="sm"
            variant={label === preset ? 'primary' : 'outline-secondary'}
            disabled={disabled || saving}
            onClick={() => setLabel(preset)}
          >
            {preset}
          </Button>
        ))}
      </div>

      <Form.Control
        type="text"
        size="sm"
        maxLength={80}
        placeholder="Custom label"
        value={label}
        disabled={disabled || saving}
        onChange={(e) => setLabel(e.target.value)}
      />

      <fieldset
        className="seat-labels-form__scope"
        disabled={disabled || saving}
      >
        <legend>Apply to</legend>
        <Form.Check
          type="radio"
          id="seat-label-scope-event"
          name="seat-label-scope"
          label="This event only"
          checked={scope === 'event'}
          onChange={() => setScope('event')}
        />
        <Form.Check
          type="radio"
          id="seat-label-scope-all"
          name="seat-label-scope"
          label="Every event using this seatmap"
          checked={scope === 'all'}
          onChange={() => setScope('all')}
        />
      </fieldset>

      {error && <p className="seat-labels-form__error">{error}</p>}

      <div className="seat-labels-form__actions">
        <Button
          type="button"
          size="sm"
          variant="primary"
          disabled={disabled || saving || !label.trim()}
          onClick={() => handleApply(label.trim())}
        >
          {saving ? 'Saving…' : 'Apply label'}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="link"
          className="text-danger"
          disabled={disabled || saving || (!current.label && !current.mixed)}
          onClick={() => handleApply('')}
        >
          Clear
        </Button>
      </div>
    </div>
  )
}
